import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import { IconPlus } from '@tabler/icons';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useAuth } from '../../auth';
import {
  useActiveAssumedRoles,
  useAssumedRoles,
  useEndAssumedRole,
} from '../../data/useInternalAdmin';
import { canPerform } from '../../utils/internalPermissions';
import AQDataGrid from '../AQDataGrid';
import AssumeRoleDialog from './AssumeRoleDialog';

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Admin',
};

const ROLE_COLORS: Record<string, 'default' | 'primary' | 'warning'> = {
  viewer: 'default',
  creator: 'primary',
  'account-admin': 'warning',
};

const InternalAssumedRoles: React.FC = () => {
  const { user } = useAuth();
  const internalRole = user?.internal_role;

  const activeQuery = useActiveAssumedRoles();
  const historyQuery = useAssumedRoles();
  const endRole = useEndAssumedRole();

  const [assumeOpen, setAssumeOpen] = React.useState(false);

  const activeRoles = activeQuery.data?.data || [];
  const allRoles = historyQuery.data?.data || [];

  const historyRows = React.useMemo(
    () =>
      allRoles.map((role) => ({
        id: role.id,
        account_name: role.attributes.account_name,
        assumed_role: role.attributes.assumed_role,
        reason: role.attributes.reason,
        started_at: role.attributes.started_at,
        ended_at: role.attributes.ended_at,
        expires_at: role.attributes.expires_at,
        _isActive: !role.attributes.ended_at,
      })),
    [allRoles],
  );

  const colDefs = React.useMemo(
    () => [
      {
        field: 'account_name',
        headerName: 'Account',
        flex: 1,
        minWidth: 160,
        sortable: true,
        filterable: true,
      },
      {
        field: 'assumed_role',
        headerName: 'Role',
        width: 130,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Chip
            label={ROLE_LABELS[params.value] || params.value}
            size="small"
            color={ROLE_COLORS[params.value] || 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'reason',
        headerName: 'Reason',
        flex: 2,
        minWidth: 200,
        sortable: false,
        filterable: false,
      },
      {
        field: 'started_at',
        headerName: 'Started',
        width: 160,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Typography variant="body2" color="text.secondary">
            {formatDistanceToNow(parseJSON(params.value), {
              addSuffix: true,
            })}
          </Typography>
        ),
      },
      {
        field: 'ended_at',
        headerName: 'Ended',
        width: 160,
        sortable: true,
        filterable: false,
        renderCell: (params: any) =>
          params.value ? (
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.value), {
                addSuffix: true,
              })}
            </Typography>
          ) : (
            <Chip label="Active" size="small" color="success" />
          ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params: any) =>
          params.row._isActive ? (
            <Button
              size="small"
              color="error"
              onClick={() => endRole.mutate(params.row.id)}
              disabled={endRole.isPending}
            >
              Deactivate
            </Button>
          ) : null,
      },
    ],
    [endRole],
  );

  const gridProps = React.useMemo(
    () => ({
      getEstimatedRowHeight: () => 52,
      getRowHeight: () => 'auto' as const,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
    }),
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Assumed Roles</Typography>
        {canPerform(internalRole, 'assume_role') && (
          <Button
            variant="contained"
            size="small"
            startIcon={<IconPlus size={16} />}
            onClick={() => setAssumeOpen(true)}
          >
            Assume Role
          </Button>
        )}
      </Box>

      {/* Active roles section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Active Roles
      </Typography>

      {activeRoles.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No active assumed roles.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          {activeRoles.map((role) => (
            <Card key={role.id} sx={{ minWidth: 280, maxWidth: 360 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {role.attributes.account_name}
                  </Typography>
                  <Chip
                    label={
                      ROLE_LABELS[role.attributes.assumed_role] ||
                      role.attributes.assumed_role
                    }
                    size="small"
                    color={
                      ROLE_COLORS[role.attributes.assumed_role] || 'default'
                    }
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {role.attributes.reason}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expires{' '}
                  {formatDistanceToNow(parseJSON(role.attributes.expires_at), {
                    addSuffix: true,
                  })}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  color="error"
                  onClick={() => endRole.mutate(role.id)}
                  disabled={endRole.isPending}
                >
                  Deactivate Role
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* History table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        History
      </Typography>

      <AQDataGrid
        id="internal-assumed-roles"
        rows={historyRows}
        colDefs={colDefs}
        loading={historyQuery.isLoading}
        gridProps={gridProps}
        customErrorElement={
          historyQuery.isError ? (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Failed to load assumed roles
            </Typography>
          ) : undefined
        }
        sx={{
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '12px',
          },
        }}
      />

      <AssumeRoleDialog
        open={assumeOpen}
        onClose={() => setAssumeOpen(false)}
        activeCount={activeRoles.length}
      />
    </Box>
  );
};

export default InternalAssumedRoles;
